<template>
  <div class="upload-essay-view">
    <eg-b-container>
      <section class="p-0">
        <div>
          <eg-b-row>
            <eg-b-col>
              <eg-b-alert v-if="error" variant="danger" show>{{
                error
              }}</eg-b-alert>

              <!-- Custom header for manual grading -->
              <div v-if="isManualGrading" class="manual-grading-header">
                <div class="back-to-dashboard mb-3">
                  <eg-b-button
                    variant="link"
                    class="back-link p-0"
                    @click="goBackToDashboard"
                  >
                    <eg-b-icon icon="arrow-left" class="me-2"></eg-b-icon>
                    &nbsp;&nbsp;{{ $t("common.backToHome") }}
                  </eg-b-button>
                </div>
                <h1 class="page-title mb-4">{{ $t("common.startGrading") }}</h1>
              </div>

              <!-- Use StartGradingContainer for workspace context -->
              <StartGradingContainer v-if="!isManualGrading">
                <essay-submission-form
                  ref="form"
                  :selected-rubric="selectedRubric"
                  :submitting="submitting"
                  @forceExit="forceExit"
                  @essaySubmitted="getFeedback"
                  @selectRubric="selectRubric"
                />
              </StartGradingContainer>

              <!-- Direct form for manual grading -->
              <essay-submission-form
                v-if="isManualGrading"
                ref="form"
                :selected-rubric="selectedRubric"
                :submitting="submitting"
                @forceExit="forceExit"
                @essaySubmitted="getFeedback"
                @selectRubric="selectRubric"
              />
            </eg-b-col>
          </eg-b-row>
        </div>
      </section>
    </eg-b-container>
    <rubric-library
      :value="selectedRubric"
      show-add-new
      @input="selectRubric"
    />
  </div>
</template>

<script>
import leaveMixin from "@/mixins/leaveMixin";
import EssaySubmissionForm from "@/components/dashboard/essay_submission_form.vue";
import StartGradingContainer from "@/components/start-grading/start_grading_container.vue";

import RubricLibrary from "@/components/rubrics/rubric_library.vue";
import { feedback } from "@/api";
import { LSK_SUGGESTION_FORM_SELECTED_RUBRIC, PLATFORMS } from "@/constants";

export default {
  name: "UploadEssaysView",

  components: {
    EssaySubmissionForm,
    RubricLibrary,
    StartGradingContainer,
  },

  mixins: [leaveMixin],

  inject: ["workspacesProvider"],

  data() {
    return {
      error: "",
      hasUpgraded: "",
      submitting: false,
      // NOTE: needed when remember shared rubric
      selectedRubric: JSON.parse(
        localStorage.getItem(LSK_SUGGESTION_FORM_SELECTED_RUBRIC)
      ),
    };
  },

  computed: {
    featureFlags() {
      return this.$store.state.featureFlags;
    },

    isManualGrading() {
      return this.$route.name === "manual-grading";
    },

    workspaceKey() {
      return this.$route.params.workspaceKey || PLATFORMS.ESSAYGRADER;
    },
  },

  methods: {
    goBackToDashboard() {
      this.$router.push({ name: "dashboard" });
    },
    forceExit() {
      this.forceIsDirty = false;
    },

    selectRubric(rubric) {
      this.selectedRubric = rubric;
    },
    // override for leave guard
    getIsDirty() {
      const rubricPreviewModalId = "modal-rubric-preview";
      const isRubricPreviewOpen =
        this.$egModal._registeredModals[rubricPreviewModalId];
      const isDirty = this.$refs.form.isDirty || isRubricPreviewOpen;

      return isDirty;
    },

    async getFeedback(payload) {
      try {
        this.submitting = true;
        await feedback({
          folderId: payload.folderId,
          gradingDetails: payload.gradingDetails,
          files: payload.files,
        });
        this.submitting = false;

        this.error = "";
        // always ignore leave guard
        this.forceIsDirty = false;

        if (payload.createdFolder) {
          // add newly created assignment folder to its parent
          if (payload.createdFolder.parentId) {
            this.workspacesProvider.addFolderWhere(
              PLATFORMS.ESSAYGRADER,
              (node) => +node.id === +payload.createdFolder.parentId,
              { ...payload.createdFolder, hasGradingDetails: 1 }
            );
          } else {
            payload.createdFolder.children[0].hasGradingDetails = 1;

            // add class and assignment
            this.workspacesProvider.addFolderWhere(
              PLATFORMS.ESSAYGRADER,
              () => false, // all false means add to root
              payload.createdFolder
            );
          }
        } else {
          this.workspacesProvider.updateFolderById(
            this.workspaceKey,
            payload.folderId,
            {
              gradingDetails: payload.gradingDetails,
              hasGradingDetails: 1,
            }
          );
        }

        this.$router.push({
          name: "workspace",
          params: {
            workspaceKey: this.workspaceKey,
            id: payload.folderId,
          },
        });

        // this.$router.push("/essays");
      } catch (error) {
        this.submitting = false;
        // error handled on the interceptors
        return;
      }
    },
  },
};
</script>
<style scoped lang="scss">
.upload-essay-view {
  min-height: 100vh;
  height: max-content;
}

.manual-grading-header {
  padding: 0;

  .back-link {
    color: var(--color-text-neutral-on-white, #1c0f30);
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;

    &:hover {
      color: var(--color-primary, #532cb5);
      text-decoration: none;
    }

    &:focus {
      box-shadow: none;
      text-decoration: none;
    }
  }

  .page-title {
    margin: 0;
    color: var(--color-text-neutral-on-white, #1c0f30);
    font-family: var(--typography-font-family-text-primary, Inter);
    font-size: var(--typography-font-size-fs-700, 24px);
    font-style: normal;
    font-weight: var(--typography-font-weight-fw-600, 600);
    line-height: 120%;
  }
}
</style>
